import React, {FC, ReactNode, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {CustomError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {isTeamOrGlobalNamespace} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {BulkActionTypesEnum} from '../../../../helpers/bulkActionHelpers';
import {TagModel} from '../../../../models/api/tagModel';
import {BulkActionConfirmationDialog} from '../../../inboxes/viewer/conversation/actions/bulk/bulkActionConfirmationDialog';
import {TagTemplate} from '../../tags/helpers';
import {CreateTagDropdownContainer} from './createTagDropdownContainer';

/*
 * Props.
 */

interface CreateConversationTagDropdownProps extends DropdownProps {
  conversationsNamespace: string;
  initialName?: string;
  parentTag?: TagModel;
  createTagAsync: (template: TagTemplate) => Promise<void>;
  onBackButtonClick: () => void;
  backButtonLabel: ReactNode;
  isBulkActionRequired: boolean;
}

/*
 * Component.
 */

export const CreateConversationTagDropdown: FC<CreateConversationTagDropdownProps> = (props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [tagError, setTagError] = useState<CustomError>();
  const [tagToBulkSave, setTagToBulkSave] = useState<TagTemplate>();

  const onSaveTag: (template: TagTemplate) => void = async (tag) => {
    setIsSaving(true);
    setTagError(undefined);

    try {
      await props.createTagAsync(tag);

      if (props.onRequestClose) {
        props.onRequestClose();
      }
    } catch (error) {
      if (!(error instanceof CustomError)) {
        throw error;
      }

      // In case of conflict with an existing tag, display an error.
      setIsSaving(false);
      setTagError(error);
    }
  };

  const onOkClick = (tagTemplate: TagTemplate, hasPreventPinTeamTags: boolean) => {
    const tag = {
      ...tagTemplate,
      ...(hasPreventPinTeamTags && isTeamOrGlobalNamespace(tagTemplate.namespace)
        ? {showInWorkspaces: false}
        : {}),
    };

    if (props.isBulkActionRequired) {
      setTagToBulkSave(tag);
      return;
    }

    onSaveTag(tag);
  };

  return (
    <>
      <CreateTagDropdownContainer
        {...props}
        namespace={props.conversationsNamespace}
        resourceNamespace={props.conversationsNamespace}
        title={<Trans id="tag-create-title">Create new tag</Trans>}
        okButtonLabel={<Trans id="tag-create-ok">Create</Trans>}
        onOkClick={onOkClick}
        isSaving={isSaving}
        tagError={tagError}
      />
      {tagToBulkSave && (
        <BulkActionConfirmationDialog
          actionType={BulkActionTypesEnum.TAG}
          onConfirmClick={() => {
            setTagToBulkSave(undefined);
            onSaveTag(tagToBulkSave);
          }}
          onRequestClose={() => setTagToBulkSave(undefined)}
        />
      )}
    </>
  );
};
